import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

import { SharedService } from '@services/shared.service';
import { ApiAccountService } from '@services/api-account.service';
import { ApiAuthService } from '@services/api-auth.service';

import { PageBase } from '@shared/page-base.component';
import { RegisterModel } from '@models/RegisterModel';
import { ApiCustomerService } from '@services/api-customer.service';
import { CustomerFindResultModel, CustomerWithoutRegisterModel } from '@shared/models/CustomerModel';
import { CouponModel } from '@shared/models/CouponModel';
import { Country } from '@shared/models/Enums';


@Component({
    selector: 'app-register-new',
    templateUrl: './register-new.component.html',
    styleUrls: ['./register.component.scss']
})
export class RegisterNewComponent extends PageBase implements OnInit {

    // Se setea cuando la verificación por email es completada
    @Input() createFromEmail: string = null;
    // Se setea cuando la verificación por teléfono es completada
    @Input() createFromPhone: { number: string, countryCode: string, dialCode: string };

    @Input() customerWithoutRegister: CustomerWithoutRegisterModel;

    @Input() isApp: boolean = true;

    @Input() isPreregisterCoupon: CouponModel = null;// receive the givi to pre register it

    // Si es true se piden los campos name y lastName en el formulario
    @Input() requestName: boolean = false;

    // Paso actual del registro (1 new, 2 account, 3 address)
    @Output() registerStep = new EventEmitter<number>();

    @Output() registeredEmail = new EventEmitter<String>();
    
    @Input() country: Country = Country.CR;  

    // Se utiliza para manejar el estado de la verificación
    isVerificationSuccess = false;

    inviteCode: string = null;

    emailDuplicated = false;
    
    customerPhoneDuplicated: CustomerFindResultModel;

    @Input() isPhoneVerification = true;

    constructor(public location: Location,
        public router: Router,
        public sharedService: SharedService,
        private apiAccountService: ApiAccountService,
        private apiCustomerService: ApiCustomerService,
        private apiAuthService: ApiAuthService) {
        super(location, router, sharedService);

        this.sharedService.setPageTitle(null);
    }

    ngOnInit(): void {

        this.sharedService.setHideHamburgerMenu(true);
        this.sharedService.setHideLoginBtn(false);
        this.sharedService.setHideNav(false);
    }


    onCheckEmail(registerModel: RegisterModel): void {

        this.emailDuplicated = false;

        this.apiCustomerService.findCustomerByEmail(registerModel.email).then(
            (customerName: string) => {

                //if the call succeeds the email was already registered
                this.emailDuplicated = true;

            }, (err) => {

                //If the call returns a not found means that the email wasnt registered yet
                this.emailDuplicated = false;
            }
        ); 

    }

    onCheckPhone(registerModel: RegisterModel): void {

        this.customerPhoneDuplicated = null;

        this.apiCustomerService.findCustomerByPhone(registerModel.phoneCountry, registerModel.phone).then(
            (customerResult: CustomerFindResultModel) => {

                //if the call succeeds the phone was already registered
                this.customerPhoneDuplicated = customerResult;

            }, (err) => {

                //If the call returns a not found means that the phone wasnt registered yet
                this.customerPhoneDuplicated = null;
            }
        );
    }

    onVerifyEmail(registerModel: RegisterModel): void {

        this.isPhoneVerification = false;

        this.sharedService.setLoading(true);

        this.apiAccountService.verifyEmail(registerModel).then(
            () => {

                // Cuando la cuenta es creada desde el modal de recibir un Givi 
                if (this.createFromEmail || this.createFromPhone) this.registerStep.emit(1);

                this.isVerificationSuccess = true;
                this.emailDuplicated = false;

                this.sharedService.setLoading(false);
            },
            (error: any) => {

                this.isVerificationSuccess = false;

                this.sharedService.setLoading(false); // hide loader

                if (typeof error?.error == "string") {

                    if (error?.error.toLowerCase().indexOf("vinculado") >= 0) {
                        this.emailDuplicated = true;
                        const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    } else if (error?.error.toLowerCase().indexOf("requerido") >= 0) {
                        const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    }
                } else {
                    // display error
                    const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error verificando el email.');
                    this.sharedService.showErrorAlert(null, errMsg);
                }
            }
        );
    }

    onVerifyPhone(registerModel: RegisterModel): void {

        // Cuando la cuenta es creada desde el modal de recibir un Givi 
        if (this.createFromEmail || this.createFromPhone) this.registerStep.emit(1);

        this.isVerificationSuccess = true;
        this.customerPhoneDuplicated = null;

        this.isPhoneVerification = true;

        this.sharedService.setLoading(true);

        this.apiAccountService.verifyPhone(registerModel).then(
            () => {

                // Cuando la cuenta es creada desde el modal de recibir un Givi 
                if (this.createFromEmail || this.createFromPhone) this.registerStep.emit(1);

                this.isVerificationSuccess = true;
                this.customerPhoneDuplicated = null;

                this.sharedService.setLoading(false);
            },
            (error: any) => {

                this.isVerificationSuccess = false;

                this.sharedService.setLoading(false); // hide loader

                if (typeof error?.error == "string") {

                    if (error?.error.toLowerCase().indexOf("vinculado") >= 0) {
                        // Simular que se retorno un usuario vinculado
                        this.customerPhoneDuplicated = new CustomerFindResultModel({ id: -1 });
                        const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    } else if (error?.error.toLowerCase().indexOf("requerido") >= 0) {
                        const errMsg = `${error.error}, revisá la información e intenta nuevamente.`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    }
                } else {
                    // display error
                    const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error verificando el teléfono.');
                    this.sharedService.showErrorAlert(null, errMsg);
                }
            }
        );
    }

    onSave(registerModel: RegisterModel): void {

        if (registerModel) { 

            this.sharedService.setLoading(true);

            this.apiAccountService.registerCustomer(registerModel).then(
                (result: RegisterModel) => {
                    
                    registerModel = result;

                    //if it is an pre register we make an output to make the call in checkout-community.comp
                    if(this.isPreregisterCoupon){

                        this.sharedService.setLoading(false); 
                        this.registeredEmail.emit(registerModel.email);

                    }else{

                        const clientId = this.isPhoneVerification ? "giviappphone-verify" : "giviappemail-verify";
                        const userName = this.isPhoneVerification ? registerModel.phone : registerModel.email;

                        this.apiAuthService.login(userName, registerModel.password, false, clientId).then(
                            () => {
                                
                                this.sharedService.setLoading(false);

                                // Cuando la cuenta es creada normalmente
                                if (this.isApp) {

                                    // navigate to account info
                                    return this.router.navigate(['/registro/cuenta']);
                                }
                                // Cuando la cuenta es creada desde el modal de recibir un Givi 
                                else {

                                    // navigate to account info into receive register
                                    this.registeredEmail.emit(registerModel.email);
                                    this.registerStep.emit(2);
                                    return
                                } 
                            },
                            (err: any) => {

                                this.sharedService.setLoading(false);

                                // display error
                                const errMsg = this.apiAuthService.getErrorMsg(err, 'El nombre de usuario o contraseña es inválido.');
                                this.sharedService.showErrorAlert(null, errMsg).then(() => this.apiAuthService.logout(this.router));
                            }
                        );
                    } 
                },
                (error: any) => {

                    this.sharedService.setLoading(false);

                    if (typeof error?.error == "string" && error?.error.toLowerCase().indexOf("es inválido") >= 0) {
                        const errMsg = `${error.error}. Por favor volvé a enviar el código de verificación`;
                        this.sharedService.showErrorAlert(null, errMsg);
                    } else {
                        // display error
                        const errMsg = this.apiAccountService.getErrorMsg(error, 'Ocurrió un error al registrar la cuenta.');
                        this.sharedService.showErrorAlert(null, errMsg);
                    }
                }
            );
        }
    }
}
